import React, { createContext, useMemo, useContext, type ReactNode } from 'react';

type FeaturesContext = {
	featureView: 'timeline' | 'timeline-embed';
	hasExportButton: boolean;
	hasInlineCreate: boolean;
	hasCreateSubtask: boolean;
	hasInviteButton: boolean; // delete when cleaning up fg('visual-refresh')
	hasIssueLinking: boolean;
	hasOnboarding: boolean;
	hasPresence: boolean;
	hasScheduleIssues: boolean;
	hasShareButton: boolean;
	inlineCreateInteraction: 'default' | 'openNewTab';
	inviteButtonInteraction: 'default' | 'openNewTab';
	issueViewInteraction: 'default' | 'openNewTab';
	stickyInlineCreateInteraction: 'default' | 'openNewTab';
	hasFilterCustomFields: boolean;
};

const defaultFeaturesContext: FeaturesContext = {
	featureView: 'timeline',
	hasExportButton: true,
	hasInlineCreate: true,
	hasCreateSubtask: true,
	hasInviteButton: true, // delete when cleaning up fg('visual-refresh')
	hasIssueLinking: true,
	hasOnboarding: true,
	hasPresence: true,
	hasScheduleIssues: true,
	hasShareButton: true,
	inlineCreateInteraction: 'default',
	inviteButtonInteraction: 'default',
	issueViewInteraction: 'default',
	stickyInlineCreateInteraction: 'default',
	hasFilterCustomFields: true,
};

const TimelineFeaturesContext = createContext<FeaturesContext>(defaultFeaturesContext);

const useTimelineFeatures = () => useContext(TimelineFeaturesContext);

type Props = Partial<FeaturesContext> & {
	children: ReactNode;
};

const TimelineFeaturesProvider = ({ children, ...props }: Props) => {
	const value = useMemo(() => ({ ...defaultFeaturesContext, ...props }), [props]);

	return (
		<TimelineFeaturesContext.Provider value={value}>{children}</TimelineFeaturesContext.Provider>
	);
};

export default TimelineFeaturesProvider;
export { useTimelineFeatures };
